import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { DownloadProgress } from '../hooks/caseHook';

interface UploadProgress {
  percentage: number;
}

export enum DOWNLOAD_STATUS {
  STARTED,
  IN_PROGRESS,
  COMPRESS,
  COMPLETED,
  FAILED,
}
export enum UPLOAD_STATUS {
  STARTED,
  IN_PROGRESS,
  COMPLETED,
  FAILED,
}
export interface CommonProgressStack {
  id: string;
  message: string;
}
export type DownloadProgressStack = CommonProgressStack & {
  type: 'DOWNLOAD';
  progress?: DownloadProgress;
  status: DOWNLOAD_STATUS;
};
export type UploadProgressStack = CommonProgressStack & {
  type: 'UPLOAD';
  progress?: UploadProgress;
  status: UPLOAD_STATUS;
};
export type ProgressStack = DownloadProgressStack | UploadProgressStack;

export interface ProgressStackContextType {
  openTray: boolean;
  toggle: boolean;
  setToggle: (toggle: boolean) => void; // shrink/expand modal by using mui Collapse
  stacks: ProgressStack[];
  addStack: (stack: ProgressStack) => void;
  updateStack: (stack: Partial<ProgressStack>) => void;
  removeStack: (stackId: string) => void;
}
export const ProgressStackContext =
  createContext<ProgressStackContextType | null>(null);
export const useProgressStackContext = () => {
  const context = useContext(ProgressStackContext);
  if (context === null) {
    throw new Error('Use useProgressStackContext inside Provider');
  }
  return context;
};
export const ProgressStackProvider = ({ children }: PropsWithChildren) => {
  const [openTray, setOpenTray] = useState<boolean>(false);
  const [toggle, setToggle] = useState<boolean>(false);
  const [stacks, setStacks] = useState<ProgressStack[]>([]);

  const addStack = useCallback((stack: ProgressStack) => {
    setOpenTray(true);
    setToggle(true);
    setStacks(prev => {
      const newStacks = [...prev];
      newStacks.push(stack);
      return newStacks;
    });
  }, []);

  const updateStack = useCallback((stack: Partial<ProgressStack>) => {
    setStacks(prev => {
      const newStack = [...prev];
      const updatedStackIndex = newStack.findIndex(s => s.id === stack.id);
      if (updatedStackIndex !== -1) {
        newStack[updatedStackIndex] = {
          ...newStack[updatedStackIndex],
          ...stack,
        } as ProgressStack;
      }
      return newStack;
    });
  }, []);

  const removeStack = useCallback((id: string) => {
    setStacks(prev => {
      const newStacks = [...prev];
      return newStacks.filter(stack => stack.id !== id);
    });
  }, []);

  useEffect(() => {
    if (openTray && stacks.length === 0) {
      setOpenTray(false);
    }
  }, [openTray, stacks.length]);

  return (
    <ProgressStackContext.Provider
      value={{
        openTray,
        toggle,
        setToggle,
        stacks,
        addStack,
        updateStack,
        removeStack,
      }}>
      {children}
    </ProgressStackContext.Provider>
  );
};
